import { useState, useEffect, useRef, useContext, type ReactNode } from "react";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassInputControl, GlassInputGroup } from "../../components/GlassInput";
import { SelectLayer } from "../../components/CommonInputs";
import { MapitStateContext } from "src/states/MapitState";
import type { GeoJSONSource } from "maplibre-gl";
import { buffer } from "@turf/turf";
import { GlassButton } from "src/components/GlassButtons";
import { AreaUnit, PriceMode } from "./JPBState";
import { Spinner } from "react-bootstrap";
import { MapitUtils } from "src/managers/MapitUtils";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";

type JPBSearchAreaProps = {
   selectableLayers: any[];
   onBufferGeometryChange: (geometry: any) => void;
   onSelectedLayerChange?: (layerId: number | undefined) => void;
   propertiesCount?: number;
   areaUnit: AreaUnit;
   onAreaUnitChange: (unit: AreaUnit) => void;
   priceMode: PriceMode;
   onPriceModeChange: (mode: PriceMode) => void;
   onExportReport?: () => void;
   isGeneratingReport?: boolean;
}

type VerticalChoiceOption<TValue extends string> = {
   value: TValue;
   label: ReactNode;
   ariaLabel?: string;
};

function VerticalConnectedChoice<TValue extends string>(props: {
   ariaLabel: string;
   value: TValue;
   options: readonly VerticalChoiceOption<TValue>[];
   onChange: (value: TValue) => void;
}) {
   return (
      <div className="jpb-choice" role="radiogroup" aria-label={props.ariaLabel}>
         <div className="jpb-choice__switch" aria-hidden="false">
            {props.options.map((opt) => {
               const active = opt.value === props.value;
               const a11yLabel =
                  (typeof opt.label === "string" ? opt.label : opt.ariaLabel) ?? String(opt.value);
               return (
                  <button
                     key={opt.value}
                     type="button"
                     role="radio"
                     aria-checked={active}
                     aria-label={a11yLabel}
                     className={"jpb-choice__btn" + (active ? " jpb-choice__btn--active" : "")}
                     onClick={() => props.onChange(opt.value)}
                  />
               );
            })}
         </div>
         <div className="jpb-choice__labels">
            {props.options.map((opt) => {
               const active = opt.value === props.value;
               return (
                  <span
                     key={opt.value}
                     className={"jpb-choice__label" + (active ? " jpb-choice__label--active" : "")}
                     onClick={() => props.onChange(opt.value)}
                  >
                     {opt.label}
                  </span>
               );
            })}
         </div>
      </div>
   );
}

export function JPBSearchArea(props: JPBSearchAreaProps) {
   const { state: mapitState } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const defaultRadiusKm = (SettingsManager.getSystemSetting("jpb") && (SettingsManager.getSystemSetting("jpb") as any).defaultRadiusKm) || 10;
   const [selectedLayer, setSelectedLayer] = useState<number | undefined>(undefined);
   const [bufferDistance, setBufferDistance] = useState<number>(defaultRadiusKm);
   const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
   const exportLinkRef = useRef<HTMLAnchorElement | null>(null);
   // Cache buffer distances per layer ID
   const bufferCacheRef = useRef<Map<number, number>>(new Map());

   const getDefaultBufferForLayer = (layerId: number | undefined): number => {
      if (layerId === undefined) return defaultRadiusKm;
      const layer = mapitState.layers[layerId];
      if (!layer) return defaultRadiusKm;
      return MapitUtils.isPointLayer(layer.type) ? 10 : 0;
   };

   useEffect(() => {
      if (selectedLayer !== undefined) {
         if (bufferCacheRef.current.has(selectedLayer)) {
            setBufferDistance(bufferCacheRef.current.get(selectedLayer)!);
         } else {
            const defaultBuffer = getDefaultBufferForLayer(selectedLayer);
            setBufferDistance(defaultBuffer);
            bufferCacheRef.current.set(selectedLayer, defaultBuffer);
         }
      }
   }, [selectedLayer, mapitState.layers]);

   // Removed auto-selection of first layer - user must manually select to avoid accidentally choosing large layers
   // useEffect(() => {
   //    if (selectedLayer === undefined && props.selectableLayers.length > 0) {
   //       setSelectedLayer(props.selectableLayers[0].layerId);
   //    }
   // }, [props.selectableLayers, selectedLayer]);

   useEffect(() => {
      props.onSelectedLayerChange?.(selectedLayer);
   }, [selectedLayer, props.onSelectedLayerChange]);

   useEffect(() => {
      if (mapitState.map) {
         const map = mapitState.map.getMapPop();
         addSearchAreaOutline(map, "jpb-search-area", "#d6c251");
         return () => {
            removeSearchAreaLayer(map, "jpb-search-area");
         };
      }
   }, [mapitState.map]);

   // Update search area geometry when layer or buffer changes
   useEffect(() => {
      if (mapitState.map && selectedLayer) {
         const geom = mapitState.layers[selectedLayer]?.geoJson;
         const map = mapitState.map.getMapPop();
         if (geom) {
            const bufferedGeom = buffer(geom, bufferDistance || 0, { units: 'kilometers' });
            setSearchAreaGeometry(map, "jpb-search-area", bufferedGeom);

            if (debounceTimerRef.current) {
               clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
               props.onBufferGeometryChange(bufferedGeom);
            }, 500);
         } else {
            setSearchAreaGeometry(map, "jpb-search-area", null);
            if (debounceTimerRef.current) {
               clearTimeout(debounceTimerRef.current);
            }
            props.onBufferGeometryChange(null);
         }
      } else {
         if (mapitState.map) {
            const map = mapitState.map.getMapPop();
            setSearchAreaGeometry(map, "jpb-search-area", null);
            if (debounceTimerRef.current) {
               clearTimeout(debounceTimerRef.current);
            }
            props.onBufferGeometryChange(null);
         }
      }

      return () => {
         if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
         }
      };
   }, [mapitState.map, selectedLayer, bufferDistance, props.onBufferGeometryChange]);

   return (
      <>
         <div style={{ position: "relative", width: "100%" }}>
            <a ref={exportLinkRef} style={{ opacity: 0, position: "absolute", zIndex: -2 }}></a>
            <div className="jpb-search-area-layout">
               {/* Left-side controls */}
               <div className="jpb-search-area-left">
                  <div className="jpb-choice-stack">
                     <VerticalConnectedChoice
                        ariaLabel="Area unit"
                        value={props.areaUnit}
                        onChange={(unit) => props.onAreaUnitChange(unit)}
                        options={[
                           { value: AreaUnit.SquareMeter, label: "m²" },
                           { value: AreaUnit.Hectare, label: "ha" }
                        ] as const}
                     />
                     <VerticalConnectedChoice
                        ariaLabel="Price mode"
                        value={props.priceMode}
                        onChange={(mode) => props.onPriceModeChange(mode)}
                        options={[
                           { value: PriceMode.Standard, label: Localization.getText("Whole property area") },
                           { value: PriceMode.Landbrug, label: Localization.getText("Only farmland area") }
                        ] as const}
                     />
                  </div>
               </div>

               {/* Centered search inputs */}
               <div className="jpb-search-area-inputs">
                  <GlassInputGroup>
                     <div
                        className="GlassInputLabel jpb-search-area-label"
                     >
                        {Localization.getText("Map layer")}
                     </div>
                     <div className="jpb-search-area-select">
                        <SelectLayer
                           selectedLayerId={selectedLayer || ""}
                           onSelect={(layerId) => {
                              if (layerId !== undefined) {
                                 setSelectedLayer(layerId);
                              }
                           }}
                           availableLayers={props.selectableLayers}
                        />
                     </div>
                     <div className="jpb-search-area-select">
                        <GlassInputControl>
                           <input
                              type="number"
                              min="0"
                              step="1"
                              value={bufferDistance}
                              onChange={(e) => {
                                 const value = Number(e.target.value);
                                 const newValue = value >= 0 ? value : 0;
                                 setBufferDistance(newValue);
                                 // Update cache when buffer changes
                                 if (selectedLayer !== undefined) {
                                    bufferCacheRef.current.set(selectedLayer, newValue);
                                 }
                              }}
                           />
                           <span>{Localization.getText("Km")}</span>
                        </GlassInputControl>
                     </div>
                     <div className="GlassInputLabel jpb-search-area-label">
                        {Localization.getText("Buffer")}
                     </div>
                  </GlassInputGroup>
               </div>

               {/* Right-side export button */}
               <div className="jpb-search-area-right">
                  {(props.propertiesCount ?? 0) >= 2 && hasAccessToFeature(Feature.JPBRapport) && (
                     <GlassButton
                        className="jpb-search-area-right-export"
                        onClick={() => {
                           if (props.onExportReport && !props.isGeneratingReport) {
                              void props.onExportReport();
                           }
                        }}
                        disabled={props.isGeneratingReport}
                     >
                        {props.isGeneratingReport ? (
                           <>
                              <Spinner
                                 animation="border"
                                 size="sm"
                                 variant="light"
                                 style={{ width: "1em", height: "1em", marginRight: "0.5em", borderWidth: "0.15em" }}
                              />
                              <span>{Localization.getText("Generating...")}</span>
                           </>
                        ) : (
                           <span>{Localization.getText("Export PDF")}</span>
                        )}
                     </GlassButton>
                  )}
               </div>
            </div>
         </div>
      </>
   );
}



// MARK: Helpers
function removeSearchAreaLayer(map: any, id: string) {
   if (map.getLayer(id + '-fill')) {
      map.removeLayer(id + '-fill');
   }
   if (map.getLayer(id + '-outline')) {
      map.removeLayer(id + '-outline');
   }
   if (map.getSource(id)) {
      map.removeSource(id);
   }
}

function addSearchAreaOutline(map: any, id: string, color: string) {
   if (!map.getSource(id)) {
      map.addSource(id, {
         type: 'geojson',
         data: {
            type: 'FeatureCollection',
            features: []
         }
      });
   }
   if (!map.getLayer(id + '-outline')) {
      map.addLayer({
         id: id + '-outline',
         type: 'line',
         source: id,
         paint: {
            'line-color': color,
            'line-width': 2
         }
      });
   }
}

function setSearchAreaGeometry(map: any, id: string, geom?: any | null) {
   const source = map.getSource(id) as GeoJSONSource;
   if (source) {
      if (!geom) {
         source.setData({
            type: 'FeatureCollection',
            features: []
         } as any);
         return;
      }
      source.setData(geom as any);
   }
}
